import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useAuth } from "@/shared/auth/auth.context";
import { getEntitlements } from "@/shared/entitlements/entitlements.api";
import type { Entitlements } from "@/shared/entitlements/schemas/entitlements.schema";
import { ApiError } from "@/shared/lib/api-call";

interface EntitlementsContextValue {
  plan: string;
  features: Entitlements["features"];
  credits: Entitlements["credits"];
  seats: Entitlements["seats"];
  isLoading: boolean;
}

// Safe defaults while the query is in flight (and after an error): an
// account with no entitlements resolved yet behaves as "unlimited, nothing
// gated" rather than "everything locked" (see useFeatureEnabled), so nothing
// flickers into a locked state before the first response lands.
const DEFAULT_CREDITS: Entitlements["credits"] = { used: 0, grant: null, remaining: null };
const DEFAULT_SEATS: Entitlements["seats"] = { used: 0, cap: null };

const EntitlementsContext = createContext<EntitlementsContextValue | null>(null);

/**
 * Wraps the app with plan-gating data pulled from `GET /me/entitlements`:
 * the caller's plan, per-feature flags, AI credit pool, and seat usage.
 * Modeled on `src/shared/auth/auth.context.tsx`. Mounted after `AuthProvider`
 * in `src/app/providers.tsx` (entitlements are account-level, resolved once
 * the caller is known).
 */
export function EntitlementsProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const query = useQuery<Entitlements>({
    queryKey: ["me", "entitlements"],
    queryFn: getEntitlements,
    // Anonymous pages (login, signup) have no session, so `GET /me/entitlements`
    // is guaranteed to 401. Gating on auth state stops the request (and its
    // retry) from firing at all on those pages instead of just discarding the
    // response.
    enabled: auth.isAuthenticated,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 1;
    },
    staleTime: 30_000,
  });

  const value = useMemo<EntitlementsContextValue>(() => {
    const data = query.data ?? null;
    return {
      plan: data?.plan ?? "",
      features: data?.features ?? {},
      credits: data?.credits ?? DEFAULT_CREDITS,
      seats: data?.seats ?? DEFAULT_SEATS,
      // TanStack Query v5: `isLoading` is `isPending && isFetching`, so a
      // disabled query (anonymous caller) reports `false` here rather than
      // hanging in a perpetual loading state.
      isLoading: query.isLoading,
    };
  }, [query.data, query.isLoading]);

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export function useEntitlements(): EntitlementsContextValue {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) throw new Error("useEntitlements must be used within <EntitlementsProvider>");
  return ctx;
}
